import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Upload } from "lucide-react";
import { API, track } from "../lib/api";
import { PROJECT_TYPE_OPTIONS, INVESTMENT_OPTIONS, TIMEFRAME_OPTIONS } from "../lib/site";

const STEPS = ["Your Details", "Your Project (Optional)"];

const isPrerenderHydration = () =>
  typeof window !== "undefined" && window.__NOOR_PRERENDERED__ === true;


const inputCls =
  "w-full bg-white border border-black/15 focus:border-ember px-4 py-3 text-sm text-graphite placeholder:text-graphite/35 transition-colors duration-300 rounded-none";
const labelCls = "block font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/60 mb-2";

function Field({ label, id, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function ConsultationForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [started, setStarted] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", project_type: "", description: "",
    investment: "", timeframe: "", consent: false, company: "",
  });

  const set = (k, v) => {
    if (!started) { setStarted(true); track("consultation_form_start"); }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const stepOneValid =
    form.name.trim() && /.+@.+\..+/.test(form.email) &&
    /^[\d\s()+-]{7,18}$/.test(form.phone.trim()) &&
    form.address.trim() && form.project_type;

  const submit = async () => {
    setError("");
    if (!form.consent) {
      setError("Please confirm you agree to the privacy policy before sending.");
      setStep(0);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
      if (photo) fd.append("photo", photo);
      await API.post("/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      track("consultation_form_submit");
      track("generate_lead", { form: "consultation" });
      navigate("/request-a-consultation/thank-you", { state: { name: form.name.split(" ")[0] } });
    } catch (e) {
      setError("Something went wrong sending your enquiry. Please try again, or call us on 07782 760 279.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="consultation-form" className="bg-cream border border-black/10">
      <div className="grid grid-cols-2 border-b border-black/10">
        {STEPS.map((s, i) => (
          <div key={s} className={`px-2 py-3 md:px-4 text-center border-r last:border-r-0 border-black/10 ${i === step ? "bg-graphite text-white" : i < step ? "text-ember" : "text-graphite/40"}`}>
            <span className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase">
              {String(i + 1).padStart(2, "0")} <span className="hidden md:inline">{s}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="p-6 md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={isPrerenderHydration() ? false : { opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35 }}
          >
            {step === 0 && (
              <div className="space-y-6">
                <Field label="Full Name *" id="f-name">
                  <input id="f-name" data-testid="consultation-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
                </Field>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Telephone Number *" id="f-phone">
                    <input id="f-phone" type="tel" data-testid="consultation-phone-input" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                  </Field>
                  <Field label="Email Address *" id="f-email">
                    <input id="f-email" type="email" data-testid="consultation-email-input" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Project Postcode *" id="f-address">
                    <input id="f-address" data-testid="consultation-address-input" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} autoComplete="postal-code" />
                  </Field>
                  <Field label="Project Type *" id="f-type">
                    <select id="f-type" data-testid="consultation-type-select" className={inputCls} value={form.project_type} onChange={(e) => set("project_type", e.target.value)}>
                      <option value="">Select…</option>
                      {PROJECT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <label className="flex items-start gap-3 cursor-pointer" htmlFor="f-consent">
                  <input id="f-consent" type="checkbox" data-testid="consultation-consent-checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 accent-[#D95601] w-4 h-4" />
                  <span className="text-xs md:text-sm text-graphite/70 leading-relaxed">
                    I agree to NOOR Design &amp; Build Ltd storing and processing my details in order to respond to
                    my enquiry, as described in the <a href="/privacy-policy" className="underline underline-offset-2">Privacy Policy</a>. *
                  </span>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <p className="text-sm text-graphite/55 leading-relaxed" data-testid="consultation-optional-note">
                  Anything you add here helps us prepare for our first conversation — all of it is optional.
                </p>
                <Field label="Brief Project Description" id="f-desc">
                  <textarea id="f-desc" rows={4} data-testid="consultation-description-input" className={inputCls} placeholder="Tell us what you would like to achieve…" value={form.description} onChange={(e) => set("description", e.target.value)} />
                </Field>
                <Field label="Rough Budget Range" id="f-investment">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2" role="radiogroup" aria-label="Rough budget range">
                    {INVESTMENT_OPTIONS.map((o) => (
                      <button
                        key={o} type="button"
                        data-testid={`consultation-investment-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        onClick={() => set("investment", o)}
                        className={`px-3 py-3 text-xs md:text-sm border transition-colors duration-300 ${form.investment === o ? "bg-graphite text-white border-graphite" : "border-black/15 hover:border-graphite"}`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Rough Timescale" id="f-timeframe">
                  <select id="f-timeframe" data-testid="consultation-timeframe-select" className={inputCls} value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)}>
                    <option value="">Select…</option>
                    {TIMEFRAME_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Photograph or Drawing" id="f-photo">
                  <label htmlFor="f-photo" data-testid="consultation-upload-label" className="flex items-center gap-3 border border-dashed border-black/25 hover:border-ember px-4 py-4 cursor-pointer transition-colors duration-300">
                    <Upload size={16} strokeWidth={1.5} className="text-graphite/50" />
                    <span className="text-sm text-graphite/60">{photo ? photo.name : "Upload a photo or drawing (max 10 MB)"}</span>
                  </label>
                  <input id="f-photo" type="file" accept="image/*,.pdf" className="hidden" data-testid="consultation-upload-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </Field>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && <p data-testid="consultation-error" className="text-sm text-ember mt-6" role="alert">{error}</p>}

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-black/10">
          {step === 1 ? (
            <button
              type="button"
              onClick={() => setStep(0)}
              data-testid="consultation-back-button"
              className="inline-flex items-center gap-2 text-sm text-graphite/50 hover:text-graphite transition-colors duration-300"
            >
              <ArrowLeft size={15} /> Back
            </button>
          ) : <span />}
          {step === 0 ? (
            <button
              type="button"
              onClick={() => stepOneValid && setStep(1)}
              disabled={!stepOneValid}
              data-testid="consultation-next-button"
              className="group inline-flex items-center gap-3 bg-graphite text-white px-8 py-3.5 text-sm font-semibold tracking-wide disabled:opacity-30 hover:bg-ember transition-colors duration-300"
            >
              Continue <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : (
            <div className="flex items-center gap-5">
              <button
                type="button"
                onClick={submit}
                disabled={sending}
                data-testid="consultation-skip-button"
                className="text-sm text-graphite/55 hover:text-ember underline underline-offset-4 transition-colors duration-300"
              >
                Skip and send
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={sending}
                data-testid="consultation-submit-button"
                className="inline-flex items-center gap-3 bg-ember hover:bg-emberdark text-white px-8 py-3.5 text-sm font-semibold tracking-wide disabled:opacity-50 transition-colors duration-300"
              >
                {sending ? "Sending…" : "Request a Consultation"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
